import posthogLib from 'posthog-js';
import {
  PostHogProvider as NativePostHogProvider,
  usePostHog as usePostHogNative,
  useFeatureFlagResult as usePostHogFlagResult,
} from 'posthog-js/react';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  CONFIG,
  getAppProperties,
  getWebRecordingConsoleLog,
  getWebSessionRecording,
  registerEventSink,
  resolveFeatureFlag,
  resolveSubFlag,
  wireEventName,
  type FeatureFlagKey,
  type FeatureResult,
  type HeroAppId,
  type SubFlagKeysOf,
} from '@hero/core';

// ---------------------------------------------------------------------------
// WebPostHogProvider — one-line setup per web app
// ---------------------------------------------------------------------------

interface WebPostHogProviderProps {
  apiKey: string;
  app: HeroAppId;
  children: ReactNode;
  /**
   * When true, all flag hooks and imperative helpers resolve as enabled
   * immediately — including `live_apps` kill-switch sub-flags. PostHog still
   * initializes for analytics; only gating is bypassed.
   */
  forceFlagsEnabled?: boolean;
}

const ALWAYS_ENABLED: FeatureResult = { isEnabled: true, status: 'enabled' };

const ForceFlagsEnabledContext = createContext(false);

/** Module mirror for imperative `getFeature` / `isFeatureEnabled` outside React. */
let forceFlagsEnabledGlobal = false;

function useForceFlagsEnabled(): boolean {
  return useContext(ForceFlagsEnabledContext);
}

const isLocalDev =
  typeof window !== 'undefined' &&
  !!window.location?.hostname &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

let registeredApp: HeroAppId | null = null;

export function WebPostHogProvider({
  apiKey,
  app,
  children,
  forceFlagsEnabled = false,
}: WebPostHogProviderProps) {
  forceFlagsEnabledGlobal = forceFlagsEnabled;

  if (!posthogLib.__loaded && apiKey) {
    const { web, apiHost, personProfiles } = CONFIG.posthog;
    const recording = getWebSessionRecording(app);
    const recordingConsoleLog = getWebRecordingConsoleLog(app);

    posthogLib.init(apiKey, {
      api_host: apiHost,
      person_profiles: personProfiles,
      autocapture: web.autocapture,
      capture_pageview: web.capturePageview,
      capture_pageleave: web.capturePageleave,
      enable_recording_console_log: recordingConsoleLog,
      ...(app === 'landing' ? { advanced_disable_feature_flags: true } : {}),
      ...(recording === false
        ? { disable_session_recording: true }
        : { session_recording: recording }),
    });
  }

  if (apiKey && registeredApp !== app) {
    const appProps = getAppProperties(app, 'web', {
      environment: isLocalDev ? 'development' : 'production',
    });
    posthogLib.register(appProps);
    if (app !== 'landing') {
      posthogLib.setPersonPropertiesForFlags({
        app: appProps.app,
        business: appProps.business,
        platform: appProps.platform,
      });
    }
    registerEventSink('posthog-js', ({ event, payload }) => {
      posthogLib.capture(wireEventName(event), payload ?? {});
    });
    registeredApp = app;
  }

  return (
    <ForceFlagsEnabledContext.Provider value={forceFlagsEnabled}>
      <NativePostHogProvider client={posthogLib}>{children}</NativePostHogProvider>
    </ForceFlagsEnabledContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// usePostHog — re-export from posthog-js/react for raw client access
// ---------------------------------------------------------------------------

export const usePostHog = usePostHogNative;

// ---------------------------------------------------------------------------
// Flag resolution — single 4-state truth table shared by hook + imperative
// ---------------------------------------------------------------------------

/**
 * Resolution contract (web):
 *
 * | hasLoadedFlags | result    | source of truth                         |
 * | -------------- | --------- | --------------------------------------- |
 * | false          | undefined | registry default (cold paint, no cache) |
 * | false          | defined   | localStorage cached value (warm paint)  |
 * | true           | defined   | live PostHog value                      |
 * | true           | undefined | flag toggled OFF at project (kill ON)   |
 *
 * Always returns a populated `FeatureResult` — never `undefined`. Sub-flag
 * inheritance still applies: if the parent resolves disabled, the sub does too.
 *
 * "Loaded" deliberately requires a **non-empty** flags snapshot: an empty
 * one is what PostHog holds right after `posthog.reset()` (logout /
 * session teardown re-evaluates against a fresh anonymous ID that may
 * match no targeting rule) or after a quota-limited / errored response.
 * Treating that as the kill switch would flip `live_apps` gates to the
 * "coming soon" screen mid-session; registry defaults are the safer
 * fallback, matching cold-paint behavior.
 */
function resolveFromState(
  key: FeatureFlagKey,
  result: ReturnType<typeof usePostHogFlagResult>,
  hasLoaded: boolean,
  subFlagKey?: string,
): FeatureResult {
  let flagValue: unknown;
  let payload: unknown;

  if (result !== undefined) {
    flagValue = result.enabled ? result.variant ?? true : false;
    payload = result.payload;
  } else if (hasLoaded) {
    flagValue = false;
    payload = undefined;
  } else {
    flagValue = undefined;
    payload = undefined;
  }

  const parentResult = resolveFeatureFlag(key, flagValue, payload);

  if (subFlagKey) {
    const rawPayload =
      typeof result?.payload === 'object' && result?.payload !== null
        ? (result.payload as Record<string, unknown>)
        : undefined;
    return resolveSubFlag(key, subFlagKey, parentResult, rawPayload?.[subFlagKey]);
  }

  return parentResult;
}

/**
 * `true` when the current flags snapshot is non-empty. An empty snapshot
 * (post-`reset()` anonymous evaluation, quota-limited / errored response)
 * must not be trusted for kill-switch decisions — see the resolution
 * contract above.
 */
function flagsSnapshotNonEmpty(): boolean {
  try {
    const variants = posthogLib.featureFlags?.getFlagVariants?.();
    return !!variants && Object.keys(variants).length > 0;
  } catch {
    return false;
  }
}

function flagsTrustworthy(): boolean {
  return (posthogLib.featureFlags?.hasLoadedFlags ?? false) && flagsSnapshotNonEmpty();
}

function resolveFlag(key: FeatureFlagKey, subFlagKey?: string): FeatureResult {
  const result = posthogLib.getFeatureFlagResult(key);
  return resolveFromState(key, result, flagsTrustworthy(), subFlagKey);
}

// ---------------------------------------------------------------------------
// getFeature — imperative, works anywhere (components, handlers, utils)
// ---------------------------------------------------------------------------

/**
 * Imperative flag check — reads the current PostHog state **once** and does NOT
 * subscribe to flag changes. The calling component will NOT re-render when flags
 * load or update remotely.
 *
 * **Prefer `useFeature()`** inside React components for reactive, cache-friendly
 * flag resolution that re-renders automatically when flags change.
 *
 * Use `getFeature()` only in non-React contexts (event handlers, utilities,
 * callbacks) where a hook cannot be used.
 */
export function getFeature<K extends FeatureFlagKey>(key: K): FeatureResult;
export function getFeature<K extends FeatureFlagKey>(
  key: K,
  subFlagKey: SubFlagKeysOf<K>,
): FeatureResult;
export function getFeature(key: FeatureFlagKey, subFlagKey?: string): FeatureResult {
  if (forceFlagsEnabledGlobal) return ALWAYS_ENABLED;
  return resolveFlag(key, subFlagKey);
}

// ---------------------------------------------------------------------------
// useFlagsHydrated — reactive boolean for `posthog.featureFlags.hasLoadedFlags`
// ---------------------------------------------------------------------------

/**
 * Returns `true` once PostHog has resolved its feature-flag snapshot for the
 * current page (`/decide` response received). Reactive: re-renders the
 * caller when hydration completes.
 *
 * Use this from app-level gates that must wait for a trusted source of truth
 * before deciding whether to render the app shell or a kill-switch screen.
 * On warm paint with a localStorage cache PostHog hydrates within tens of
 * milliseconds; on a true cold paint hydration is bounded by `/decide`'s
 * round trip.
 *
 * Stays `true` once flipped (no rehydration loops).
 */
export function useFlagsHydrated(): boolean {
  const forced = useForceFlagsEnabled();
  if (forced) return true;

  const posthog = usePostHogNative();
  const [hasLoaded, setHasLoaded] = useState<boolean>(
    () => posthog?.featureFlags?.hasLoadedFlags ?? false,
  );

  useEffect(() => {
    if (!posthog) return undefined;
    setHasLoaded(posthog.featureFlags?.hasLoadedFlags ?? false);
    return posthog.onFeatureFlags(() => setHasLoaded(true));
  }, [posthog]);

  return hasLoaded;
}

// ---------------------------------------------------------------------------
// useFlagReady — per-flag readiness, true on cache hit OR after hydration
// ---------------------------------------------------------------------------

/**
 * Returns `true` as soon as PostHog has *any* decisive answer for `key` —
 * either a value persisted from a previous session (warm paint cache hit)
 * or the live `/decide` response.
 *
 * Difference vs {@link useFlagsHydrated}: this hook flips earlier on warm
 * paint, the moment PostHog reports a cached value, instead of waiting for
 * the full network round-trip. Use it in app-level gates (e.g.
 * `LiveAppSubGate`) so the user only sees the loading shell when there's
 * truly no information to act on — i.e. the very first cold paint of the
 * app on a new device / cleared cache. Subsequent loads short-circuit on
 * the cached value and render the dashboard (or block screen) immediately.
 *
 * Stays `true` once flipped (no rehydration loops).
 */
export function useFlagReady(key: FeatureFlagKey): boolean {
  const forced = useForceFlagsEnabled();
  if (forced) return true;

  const hydrated = useFlagsHydrated();
  const result = usePostHogFlagResult(key);
  return hydrated || result !== undefined;
}

// ---------------------------------------------------------------------------
// useFeature — reactive React hook, re-renders when flags change
// ---------------------------------------------------------------------------

/**
 * Reactive version of {@link flagsTrustworthy} — re-renders the caller
 * whenever the flags snapshot changes. Unlike {@link useFlagsHydrated},
 * this can flip back to `false` mid-session (e.g. `posthog.reset()` on
 * logout wipes the snapshot), at which point flag resolution falls back
 * to registry defaults instead of the kill switch.
 */
function useFlagsTrustworthy(): boolean {
  const posthog = usePostHogNative();
  const [trustworthy, setTrustworthy] = useState<boolean>(flagsTrustworthy);

  useEffect(() => {
    if (!posthog) return undefined;
    const update = () => setTrustworthy(flagsTrustworthy());
    update();
    return posthog.onFeatureFlags(update);
  }, [posthog]);

  return trustworthy;
}

/**
 * Reactive flag hook. Always returns a populated `FeatureResult` —
 * `{ isEnabled, status, variant?, payload? }` — never `undefined`.
 *
 * See {@link resolveFromState} for the full state table. In short:
 *
 * - **Cold paint** (no cache, no fetch): registry default from
 *   `FEATURE_FLAGS[key]` so the UI renders sensibly without flicker.
 * - **Warm paint** (PostHog has a cached value from localStorage): cached value.
 * - **After hydration**: live value, or `disabled` if PostHog has hydrated and
 *   the flag is missing from the response (= toggled off at project level →
 *   kill switch). Sub-flags inherit the parent's disabled state automatically.
 */
export function useFeature<K extends FeatureFlagKey>(key: K): FeatureResult;
export function useFeature<K extends FeatureFlagKey>(
  key: K,
  subFlagKey: SubFlagKeysOf<K>,
): FeatureResult;
export function useFeature(key: FeatureFlagKey, subFlagKey?: string): FeatureResult {
  const forced = useForceFlagsEnabled();
  if (forced) return ALWAYS_ENABLED;

  const result = usePostHogFlagResult(key);
  // Tracking loaded-ness as state is what lets the hook re-render when
  // hydration completes even if the flag value itself didn't change — e.g. a
  // flag toggled OFF at the project level resolves to `undefined` both pre-
  // and post-hydration but means "registry default" → "kill switch" across
  // that boundary. Only a non-empty snapshot counts (see resolution contract).
  const hasLoaded = useFlagsTrustworthy();

  return resolveFromState(key, result, hasLoaded, subFlagKey);
}

// ---------------------------------------------------------------------------
// isFeatureEnabled — imperative boolean shortcut
// ---------------------------------------------------------------------------

/**
 * Imperative boolean shortcut — reads the current PostHog state **once** and
 * does NOT subscribe to flag changes. The calling component will NOT re-render
 * when flags load or update remotely.
 *
 * **Prefer `useFeature()`** inside React components for reactive, cache-friendly
 * flag resolution that re-renders automatically when flags change.
 *
 * Use `isFeatureEnabled()` only in non-React contexts (event handlers, utilities,
 * route guards) where a hook cannot be used.
 */
export function isFeatureEnabled<K extends FeatureFlagKey>(key: K): boolean;
export function isFeatureEnabled<K extends FeatureFlagKey>(
  key: K,
  subFlagKey: SubFlagKeysOf<K>,
): boolean;
export function isFeatureEnabled(key: FeatureFlagKey, subFlagKey?: string): boolean {
  if (forceFlagsEnabledGlobal) return true;
  return resolveFlag(key, subFlagKey).isEnabled;
}
